/**
 * Contrato da barra de início e das fichas removíveis de `/revisao`.
 *
 * A barra e a view importam daqui para não inventarem props diferentes.
 * A contagem (`cartoes`) é a leitura que `GET /review/due` devolveu para a
 * seleção corrente — com `ahead=true` ela inclui cartões que ainda não
 * venceram (cmd/internal/database/review_cards.go, dueFilter). Iniciar leva
 * essa mesma seleção, não uma fila montada à parte.
 */
import type { RevisaoListDimension } from "../../../core/store/useRevisaoSelecaoStore";

/**
 * A dimensão do dia fixado (revisao-home/spec.md, "O dia aparece entre os
 * filtros escolhidos"). Nunca mora na store de seleção — `diaFixado` é
 * estado local da view — e só existe aqui para a ficha ter uma dimensão
 * própria, distinta das três que a store apaga.
 */
export const DIA_FICHA_DIMENSION = "day" as const;

export const DIA_DIMENSION_LABEL = "Dia";

export type FichaDimension = RevisaoListDimension | typeof DIA_FICHA_DIMENSION;

/**
 * Uma ficha removível. `value` é o que a store apaga; `label` é o que se lê.
 * Quando `dimension` é `DIA_FICHA_DIMENSION`, `value` é o iso do dia fixado e
 * removê-la solta o dia (design.md, Decisão 5) em vez de apagar algo da store.
 */
export interface FichaDeFiltro {
  dimension: FichaDimension;
  dimensionLabel: string;
  value: string;
  label: string;
}

export interface BarraDeInicioProps {
  /**
   * Cartões que a seleção corrente produz. É o `leitura.total` já mostrado
   * no herói — a barra não soma de novo. A duração sai de `estimativaDuracao`.
   */
  cartoes: number;
  /**
   * A leitura que produziu `cartoes` ainda não voltou. Enquanto isto for
   * verdade, zero não significa "nada a estudar" e iniciar fica indisponível.
   */
  aguardando?: boolean;
  /** Desligado por padrão. Ligado, a contagem inclui cartões que ainda não venceram. */
  ahead: boolean;
  onAhead: (ahead: boolean) => void;
  onLimpar: () => void;
  /** Há o que descartar. Limpar fica indisponível quando a seleção já está vazia. */
  podeLimpar: boolean;
  onIniciar: () => void;
  /**
   * Duas linhas, para a página estreita. A barra larga não passa isto: abaixo
   * de `md` ela já empilha, e esse empilhamento cobre a lista.
   */
  densa?: boolean;
}
